import {type ChangeEvent, useCallback, useMemo, useState} from 'react';

import {useRouter} from 'next/router';
import {useTranslation} from 'react-i18next';

import type {TFunction} from 'i18next';
import type {Category, Homepage as HomepageType, Walk} from 'types/payload-types';
import type {BlockData} from 'UI/modules/BlockRenderer';

const CENTS_PER_EURO = 100;
const SIXTY = 60;

/** Union of all possible Homepage block variants as generated from the Payload schema. */
type HomepageBlock = NonNullable<HomepageType['blocks']>[number];

/** Runtime state and handlers required by interactive homepage blocks. */
interface EnrichBlockContext {
    /** Currently active filter chip label, if any. */
    activeFilter: string | null;
    /** Walks matching the current filter/search state. */
    allTours: Walk[];
    /** Handler invoked when a filter chip is clicked. */
    handleChip(label: string): void;
    /** Handler invoked to navigate to a walk detail page. */
    navigateToWalk(slug: string): void;
}

/**
 * Enriches a single homepage block with the runtime props its interactive
 * variant needs (filter state, handlers, matching walks).
 *
 * @param block      The raw block data from the CMS.
 * @param categories Array of Category documents from the CMS.
 * @param context    Runtime state and handlers required by interactive blocks.
 * @returns The block data, enriched with runtime props where applicable.
 */
const enrichBlock = (
    block: HomepageBlock,
    categories: Category[] | undefined,
    context: EnrichBlockContext
): BlockData => {
    switch (block.blockType) {
        case 'category-filter-block': {
            const hasConfiguredCategories = block.categories && block.categories.length > 0;

            return {
                ...block,
                activeFilter: context.activeFilter,
                categories: hasConfiguredCategories ? block.categories : categories,
                onChipClick: context.handleChip
            };
        }
        case 'all-walks-block':
            return {
                ...block,
                onWalkClick: context.navigateToWalk,
                walks: context.allTours
            };
        default:
            return block;
    }
};

/** Map terrain enum values to i18n category keys (fallback when no categories assigned). */
const TERRAIN_CATEGORY_KEYS: Record<string, string> = {
    asphalt: 'home.categoryScenic',
    cobblestone: 'home.categoryHistorical',
    'forest-path': 'home.categoryNature',
    gravel: 'home.categoryNature',
    mixed: 'home.categoryDiscovery'
};

/**
 * Returns the translated filter chip labels based on available categories.
 * Falls back to hardcoded chips when no categories are provided.
 *
 * @param t          The i18next translation function.
 * @param categories Array of Category documents from the CMS.
 * @returns Array of filter chip labels.
 */
export const getFilterChips = (t: TFunction, categories?: Category[]): string[] => {
    if (categories && categories.length > 0) {
        const categoryNames = categories.map(c => c.name);

        return [...categoryNames, t('home.filterFree'), t('home.filterShort')];
    }

    return [
        t('home.categoryHistorical'),
        t('home.categoryNature'),
        t('home.filterFree'),
        t('home.filterPaid'),
        t('home.filterShort')
    ];
};

/**
 * Derives a display category from the walk's categories relationship.
 * Falls back to terrain-based mapping when no categories are assigned.
 *
 * @param walk The walk document.
 * @param t    The i18next translation function.
 * @returns A human-readable category string.
 */
export const getCategory = (walk: Walk, t: TFunction): string => {
    const cats = walk.categories;

    if (cats && Array.isArray(cats) && cats.length > 0) {
        const labels = cats
            .map(c => (typeof c === 'object' ? c.name : null))
            .filter(Boolean);

        if (labels.length > 0) return labels.join(', ');
    }

    // Fallback to terrain-based mapping
    const terrains = walk.general?.terrain;

    if (!terrains || terrains.length === 0) return t('home.categoryDiscovery');

    const labels = Array.from(new Set(terrains.map(v => TERRAIN_CATEGORY_KEYS[v]).filter(Boolean).map(key => t(key))));

    return labels.length > 0 ? labels.join(', ') : t('home.categoryDiscovery');
};

/**
 * Formats the walk price for display.
 *
 * @param walk The walk document.
 * @returns A formatted price string like `"Free"` or `"€4.99"`.
 */
export const formatPrice = (walk: Walk): string => {
    if (walk.pricing?.isFree !== false) return 'Free';

    return `€${((walk.pricing.price ?? 0) / CENTS_PER_EURO).toFixed(2)}`;
};

/**
 * Formats the walk duration for display.
 *
 * @param walk The walk document.
 * @returns A formatted duration string like `"45 min"` or `"1.5 h"`.
 */
export const formatDuration = (walk: Walk): string => {
    const mins = walk.general?.duration;

    if (!mins) return '—';
    if (mins < SIXTY) return `${mins} min`;

    return `${(mins / SIXTY).toFixed(1)} h`;
};

/**
 * Returns the cover image URL for a walk.
 *
 * @param walk The walk document.
 * @returns The image URL or a placeholder.
 */
export const getCoverUrl = (walk: Walk): string => {
    const img = typeof walk.coverImage === 'object' ? walk.coverImage : null;

    return img?.sizes?.card?.url ?? img?.url ?? '/images/placeholder.jpg';
};

/**
 * Hook für die Startseite. Kapselt Suche, Chip-Filter und Navigation.
 * Nutzt Kategorien aus dem CMS für die Filter-Chips.
 *
 * @param walks      Array aller Walks.
 * @param categories Array aller Kategorien aus dem CMS.
 * @param homepage   Das Homepage-Global-Dokument mit Block-Konfiguration.
 * @returns Gefiltertes Walk-Array, Event-Handler und Helpers.
 */
export const useHomePage = (walks: Walk[], categories?: Category[], homepage?: HomepageType | null) => {
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState<string | null>(null);
    const [cityFilter, setCityFilter] = useState('');
    const {t} = useTranslation();
    const router = useRouter();

    const filterChips = useMemo(() => getFilterChips(t, categories), [t, categories]);

    const filtered = useMemo(() => {
        const term = search.toLowerCase();
        const freeLabel = t('home.filterFree');
        const paidLabel = t('home.filterPaid');
        const shortLabel = t('home.filterShort');

        return walks.filter(w => {
            const matchSearch = !term
                || w.title.toLowerCase().includes(term)
                || w.shortDescription.toLowerCase().includes(term);

            const walkCityId = typeof w.city === 'string' ? w.city : w.city.id;
            const matchesCity = !cityFilter || walkCityId === cityFilter;

            if (!matchSearch || !matchesCity) return false;
            if (!activeFilter) return true;

            const isFree = w.pricing?.isFree !== false;
            const dur = w.general?.duration ?? 0;

            if (activeFilter === freeLabel) return isFree;
            if (activeFilter === paidLabel) return !isFree;
            if (activeFilter === shortLabel) return dur > 0 && dur < SIXTY;

            // Check if the walk has a category matching the active filter
            const walkCategories = w.categories;

            if (walkCategories && Array.isArray(walkCategories)) {
                return walkCategories.some(c => (typeof c === 'object' ? c.name : '') === activeFilter);
            }

            // Fallback to terrain-based category matching
            const cat = getCategory(w, t);

            return cat.includes(activeFilter);
        });
    }, [walks, search, activeFilter, cityFilter, t]);

    const allTours = useMemo(() => filtered, [filtered]);

    /**
     * Handler für das Suchfeld.
     *
     * @param e Das Change-Event des Inputs.
     */
    const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    }, []);

    /**
     * Handles clicking a chip filter. Toggles the filter on/off.
     *
     * @param label The chip label.
     */
    const handleChip = useCallback((label: string) => {
        setActiveFilter(prev => (prev === label ? null : label));
    }, []);

    /**
     * Handler für den Stadtfilter.
     *
     * @param e Das Change-Event des Selects.
     */
    const handleCityFilter = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
        setCityFilter(e.target.value);
    }, []);

    /**
     * Navigiert zur Walk-Detailseite.
     *
     * @param slug Der Walk-Slug.
     */
    const navigateToWalk = useCallback((slug: string) => {
        void router.push(`/walks/${slug}`);
    }, [router]);

    const enrichedBlocks = useMemo(() => {
        if (!homepage?.blocks) return null;

        return homepage.blocks.map(block => enrichBlock(block, categories, {
            activeFilter,
            allTours,
            handleChip,
            navigateToWalk
        }));
    }, [homepage, categories, activeFilter, allTours, handleChip, navigateToWalk]);

    const firstHeroIndex = useMemo(
        () => enrichedBlocks?.findIndex(block => block.blockType === 'hero-block') ?? -1,
        [enrichedBlocks]
    );

    const heroBlocks = useMemo(
        () => (firstHeroIndex >= 0 && enrichedBlocks ? [enrichedBlocks[firstHeroIndex]] : null),
        [enrichedBlocks, firstHeroIndex]
    );

    const contentBlocks = useMemo(
        () => (enrichedBlocks ? enrichedBlocks.filter((_, i) => i !== firstHeroIndex) : null),
        [enrichedBlocks, firstHeroIndex]
    );

    const isSearching = search.length > 0;

    return {
        activeFilter,
        allTours,
        cityFilter,
        contentBlocks,
        filterChips,
        filtered,
        handleChip,
        handleCityFilter,
        handleSearch,
        heroBlocks,
        isSearching,
        navigateToWalk,
        search,
        t
    };
};